import { Banner } from '@atlaskit/page-layout';
import { Banner as BannerV4 } from '@atlassian/navigation-system/layout/banner';
import React, { ReactNode, useState, useEffect, useRef, PropsWithChildren } from 'react';

import { useIsNavRefreshEnabled } from '@townsquare/stat-sig/nav4';

import { CookiesConsentBanner } from '../cookies-consent-banner';
import { ScheduledMaintenanceBanner } from '../scheduled-maintenance-banner';

import { BannerHeightContext } from './context';

function BannerComponent(props: PropsWithChildren<{ height: number; testId: string }>) {
  const isNav4 = useIsNavRefreshEnabled();

  if (isNav4) {
    return (
      <BannerV4 UNSAFE_height={props.height} testId={props.testId}>
        {props.children}
      </BannerV4>
    );
  }

  return (
    <Banner height={props.height} testId={props.testId}>
      {props.children}
    </Banner>
  );
}

export function SiteBannerProvider({ children }: { children: ReactNode }) {
  // The total height of all visible banners
  const [height, setHeight] = useState(0);

  const containerRef = useRef<HTMLDivElement>(null);
  const resizeObserver = useRef(
    new ResizeObserver(entries => {
      if (!Array.isArray(entries) || !entries.length) {
        return;
      }

      setHeight(entries[0].contentRect.height);
    }),
  );

  useEffect(() => {
    const currentObserver = resizeObserver.current;
    if (containerRef?.current) {
      currentObserver.observe(containerRef.current);
    } else {
      currentObserver.disconnect();
    }

    return () => {
      currentObserver.disconnect();
    };
  }, [containerRef, resizeObserver]);

  return (
    <BannerHeightContext.Provider value={{ height }}>
      <BannerComponent height={height} testId="site-banners-container">
        <div ref={containerRef}>
          <CookiesConsentBanner />
          <ScheduledMaintenanceBanner />
        </div>
      </BannerComponent>
      {children}
    </BannerHeightContext.Provider>
  );
}
